/**
 * onePagerResponseMapper.ts
 * Maps the raw backend response DTO into the frontend table model.
 * Isolates the UI from backend contract changes.
 *
 * Backend shape:
 *   content[].grain   → frontend grainVals (remapped keys)
 *   content[].metrics → frontend vals (remapped keys)
 *   summary           → grand total vals (same remapping)
 *   totalElements     → frontend totalRows
 */

import { GroupedRow } from '../constants/onePagerConstants';
import {
	OnePagerFlag,
	OnePagerMappedResult,
	OnePagerResponseDTO,
	OnePagerResponseRowDTO,
} from '../types/onePagerTypes';

// ─── Key Mapping: Backend → Frontend ──────────────────────────────────────────

/**
 * Maps backend metric keys to frontend PAGER_COLUMNS column IDs.
 * Any backend key not listed here passes through unchanged (fallback).
 */
const METRIC_KEY_MAP: Record<string, string> = {
	// Safety
	rir: 'rir',
	// People
	gapTime: 'gap',
	kcph: 'kcph',
	layerCph: 'lcph',
	ot: 'ot',
	timeToPunch: 'ttp',
	turnOver: 'turn',
	// Accuracy
	bpa: 'bpa',
	ihc: 'ihc',
	sia: 'sia',
	// Reliability
	fillRate: 'fill',
	onTime: 'ontime',
	// Costs
	allInWaste: 'waste',
	dps: 'dps',
	haulback: 'haul',
	layer: 'layer',
	pku: 'pku',
	repackDollar: 'repack',
	// Accuracy
	perfectTruckExecution: 'pte_execution',
	// Navigator
	pte: 'pte',
};

/**
 * Maps backend `*Bad` flag keys to frontend PAGER_COLUMNS column IDs.
 *
 * A dedicated map is required because flag names are metric names suffixed with
 * `Bad`, while several column IDs are abbreviated (`turnOverBad` → `turn`), so
 * METRIC_KEY_MAP cannot be reused. Without this the flags would pass through
 * unchanged as `rirBad` etc., never match a column ID, and every cell would
 * render black.
 *
 * Any backend key not listed here passes through unchanged (fallback).
 */
const FLAG_KEY_MAP: Record<string, string> = {
	// Lower-is-better (red when actual is above target)
	rirBad: 'rir',
	otBad: 'ot',
	turnOverBad: 'turn',
	undirectedHoursBad: 'undir',
	haulbackBad: 'haul',
	ihcBad: 'ihc',
	allInWasteBad: 'waste',
	gapTimeBad: 'gap',
	timeToPunchBad: 'ttp',
	siaBad: 'sia',
	repackDollarBad: 'repack',
	// Higher-is-better (red when actual is below target)
	onTimeBad: 'ontime',
	bpaBad: 'bpa',
	perfectTruckExecutionBad: 'pte_execution',
	kcphBad: 'kcph',
	fillRateBad: 'fill',
	pteBad: 'pte',
	dpsBad: 'dps',
	pkuBad: 'pku',
	layerBad: 'layer',
};

/**
 * Maps backend grain keys to frontend GrainKey short codes.
 * Frontend uses 'rg', 'mk', 'lc', etc. Backend uses full names.
 */
const GRAIN_KEY_MAP: Record<string, string> = {
	region: 'rg',
	market: 'mk',
	location: 'lc',
	locationType: 'lt',
	year: 'yr',
	period: 'pd',
	week: 'wk',
	date: 'dt',
};

// ─── Shared Remapping Utility ─────────────────────────────────────────────────

/**
 * Remaps a Record's keys using the provided mapping dictionary.
 * Keys not found in the map pass through unchanged.
 */
function remapKeys<T>(source: Record<string, T>, keyMap: Record<string, string>): Record<string, T> {
	const result: Record<string, T> = {};
	for (const [backendKey, value] of Object.entries(source)) {
		const frontendKey = keyMap[backendKey] ?? backendKey;
		result[frontendKey] = value;
	}
	return result;
}

// ─── Row Mapper ───────────────────────────────────────────────────────────────

/**
 * Maps a single backend row DTO to the frontend GroupedRow model.
 * Remaps both grain keys and metric keys to match frontend column IDs.
 */
function mapRow(dto: OnePagerResponseRowDTO): GroupedRow {
	const row: GroupedRow = {
		grainVals: remapKeys(dto.grain, GRAIN_KEY_MAP),
		vals: remapKeys(dto.metrics, METRIC_KEY_MAP),
		memberCount: 0,
	};

	if (dto.targets && Object.keys(dto.targets).length > 0) {
		row.targets = remapKeys(dto.targets, METRIC_KEY_MAP);
	}
	if (dto.flags && Object.keys(dto.flags).length > 0) {
		row.flags = remapKeys<OnePagerFlag>(dto.flags, FLAG_KEY_MAP);
	}

	return row;
}

// ─── Grand Total Mapper ───────────────────────────────────────────────────────

/**
 * Builds the grand total row from the backend's legacy summary triple.
 * Uses the same key maps as normal rows so colours cannot drift.
 * Returns null if summary is empty or undefined.
 */
function mapGrandTotalFromSummary(
	summary: Record<string, number> | undefined,
	summaryTargets?: Record<string, number>,
	summaryFlags?: Record<string, OnePagerFlag>,
): GroupedRow | null {
	if (!summary || Object.keys(summary).length === 0) {
		return null;
	}

	const row: GroupedRow = {
		grainVals: { _total: 'Grand Total' },
		vals: remapKeys(summary, METRIC_KEY_MAP),
		isGrandTotal: true,
		memberCount: 0,
	};

	if (summaryTargets && Object.keys(summaryTargets).length > 0) {
		row.targets = remapKeys(summaryTargets, METRIC_KEY_MAP);
	}
	if (summaryFlags && Object.keys(summaryFlags).length > 0) {
		row.flags = remapKeys<OnePagerFlag>(summaryFlags, FLAG_KEY_MAP);
	}

	return row;
}

/**
 * Resolves the grand total row, preferring the backend's row-shaped
 * `grandTotal` object over the legacy `summary`/`summaryTargets`/`summaryFlags`
 * triple.
 *
 * `grandTotal` is produced by the same flag-building call as data rows, so
 * routing it through `mapRow` guarantees the total's colours follow identical
 * rules. The legacy fallback keeps older backends working.
 */
function resolveGrandTotal(dto: OnePagerResponseDTO): GroupedRow | null {
	if (dto.grandTotal) {
		return {
			...mapRow(dto.grandTotal),
			grainVals: { _total: 'Grand Total' },
			isGrandTotal: true,
		};
	}

	return mapGrandTotalFromSummary(dto.summary, dto.summaryTargets, dto.summaryFlags);
}

// ─── Main Response Mapper ─────────────────────────────────────────────────────

/**
 * Maps the complete backend response DTO into the frontend model.
 * This is the single transformation point between API and UI.
 */
export function mapOnePagerResponse(dto: OnePagerResponseDTO): OnePagerMappedResult {
	const rows = Array.isArray(dto.content) ? dto.content.map(mapRow) : [];
	const grandTotalRow = resolveGrandTotal(dto);

	return {
		rows,
		grandTotalRow,
		totalRows: dto.totalElements ?? 0,
		totalPages: Math.max(dto.totalPages ?? 1, 1),
		page: dto.page ?? 1,
		size: dto.size ?? 25,
	};
}
